import {
  AddonFormData,
  MarketDataFormData,
  ProductAddonsReq,
  ProductData,
  ProductFormData,
  ProductMarketDataReq,
  ProductOffer,
  ProductResetReq,
  ProductTradingAccountTypes,
  ProductTypes,
  ReBillTimesType,
  ResetFormData,
  TradingPlatForms,
} from '@src/types';
import { compact, isEmpty } from 'lodash';
import omit from 'lodash.omit';

export const getIsNoneTrading = (categories: string) => {
  return categories !== ProductTypes.TRADING_ACCOUNT;
};

export const getDisableNonPerformanceAccount = (
  categories: string,
  isPerformanceAccount?: boolean | null,
  accountType?: string
) => {
  const isNonTrading = getIsNoneTrading(categories);
  const isDemo = accountType === ProductTradingAccountTypes.DEMO_ACCOUNT;
  const isPracticeOrCompetition = accountType
    ? [
        ProductTradingAccountTypes.PRACTICE_ACCOUNT,
        ProductTradingAccountTypes.COMPETITION,
      ].includes(accountType as ProductTradingAccountTypes)
    : false;

  return (
    isNonTrading || isDemo || (isPracticeOrCompetition && !isPerformanceAccount)
  );
};

export const buildProduct = (
  data: ProductFormData,
  productId?: string
): ProductData => {
  const isNonTrading = getIsNoneTrading(data.category);
  const isDisable = getDisableNonPerformanceAccount(
    data.category,
    data.is_performance_account,
    data.account_type
  );
  const isDisableSecondBilling =
    data.rebill_times_type === ReBillTimesType.NoMoreCharges;

  const isCreated = !productId;

  const payoutLogicsCreated = isCreated
    ? data.payout_logics?.map((item) => omit(item, ['id', 'product_id']))
    : data.payout_logics;

  const payoutLogics =
    !isDisable && data.payout_logics?.filter((item) => !isEmpty(item))?.length
      ? compact(payoutLogicsCreated)
      : [];

  return {
    product: {
      title: data.title,
      description: data.description,
      internal_notes: data.internal_notes ?? null,
      display_name_web: data.display_name_web ?? null,
      display_name_app: data.display_name_app ?? null,
      required_product_ids: compact(data.required_product_ids).length
        ? compact(data.required_product_ids)
        : null,
      disallow_product_ids: compact(data.disallow_product_ids).length
        ? compact(data.disallow_product_ids)
        : null,
      start_date_calculation: data.start_date_calculation ?? null,
      mailchimp_list_segments: compact(data.mailchimp_list_segments).length
        ? compact(data.mailchimp_list_segments)
        : null,
      disable_product_link: data.disable_product_link ?? null,
      max_payouts_per_account:
        !isDisable && data.max_payouts_per_account
          ? data.max_payouts_per_account
          : 0,
      category: data.category ?? null,
      type: data.type ?? null,
      is_bundle: data.is_bundle ?? null,
      account_benefits: data.account_benefits ?? null,
      accounts_in_bundle: data.accounts_in_bundle ?? null,
      weekly_account_payout_max: data.weekly_account_payout_max ?? null,
      account_type: data.account_type ?? null,
      payout_date_eligible_type:
        !isDisable && data.payout_request_trigger === 'date_eligible'
          ? (data.payout_date_eligible_type ?? null)
          : null,
      is_performance_account: data.is_performance_account ?? null,
      is_visible_on_web: data.is_visible_on_web ?? null,
      affiliate_eligible: data.affiliate_eligible ?? null,
      compatible_data_feeds: compact(data.compatible_data_feeds).length
        ? compact(data.compatible_data_feeds)
        : null,
      cash_in_hand: data.cash_in_hand ?? null,
      auto_liquidate: data.auto_liquidate ?? null,
      drawdown: data.drawdown ?? null,
      drawdown_type: data.drawdown_type ?? null,
      trailing_drawdown_stops: data.trailing_drawdown_stops ?? null,
      trailing_drawdown_stop_at: data.trailing_drawdown_stop_at ?? null,
      pa_account_trailing_drawdown_stop_at:
        data.pa_account_trailing_drawdown_stop_at ?? null,
      days_to_qualify: !isDisable ? data.days_to_qualify : null,
      profit_target: !isDisable ? data.profit_target : null,
      fill_out_survey: !isDisable ? data.fill_out_survey : null,
      survey_trigger_time: !isDisable ? data.survey_trigger_time : null,
      offer_reset_practice_account_phase: !isDisable
        ? data.offer_reset_practice_account_phase
        : null,
      offer_reset_performance_account_phase: !isDisable
        ? data.offer_reset_performance_account_phase
        : null,
      action_after_payout: !isDisable ? data.action_after_payout : null,
      payout_request_trigger: !isDisable ? data.payout_request_trigger : null,
      practice_contract_usage_enabled: !isDisable
        ? data.practice_contract_usage_enabled
        : null,
      practice_contract_not_exceed_percent: !isDisable
        ? data.practice_contract_not_exceed_percent
        : null,
      practice_contract_average_days: !isDisable
        ? data.practice_contract_average_days
        : null,
      practice_profit_withdrawal_enabled: !isDisable
        ? data.practice_profit_withdrawal_enabled
        : null,
      practice_profit_not_exceed_percent: !isDisable
        ? data.practice_profit_not_exceed_percent
        : null,
      practice_profit_average_days: !isDisable
        ? data.practice_profit_average_days
        : null,
      practice_best_day_profit_enabled: !isDisable
        ? data.practice_best_day_profit_enabled
        : null,
      practice_best_day_profit_not_exceed_percent: !isDisable
        ? data.practice_best_day_profit_not_exceed_percent
        : null,
      practice_best_day_profit: !isDisable
        ? data.practice_best_day_profit
        : null,
      performance_contract_usage_enabled: !isDisable
        ? data.performance_contract_usage_enabled
        : null,
      performance_contract_not_exceed_percent: !isDisable
        ? data.performance_contract_not_exceed_percent
        : null,
      performance_contract_average_days: !isDisable
        ? data.performance_contract_average_days
        : null,
      performance_profit_withdrawal_enabled: !isDisable
        ? data.performance_profit_withdrawal_enabled
        : null,
      performance_profit_not_exceed_percent: !isDisable
        ? data.performance_profit_not_exceed_percent
        : null,
      performance_profit_average_days: !isDisable
        ? data.performance_profit_average_days
        : null,
      performance_best_day_profit_enabled: !isDisable
        ? data.performance_best_day_profit_enabled
        : null,
      performance_best_day_profit_not_exceed_percent: !isDisable
        ? data.performance_best_day_profit_not_exceed_percent
        : null,
      performance_best_day_profit: !isDisable
        ? data.performance_best_day_profit
        : null,
      addon_type: data.addon_type ?? null,
      addon_lifetime_performance_account_package_id:
        data.addon_lifetime_performance_account_package_id
          ? Number(data.addon_lifetime_performance_account_package_id)
          : null,
      addon_performance_account_fee_option:
        data.addon_performance_account_fee_option ?? null,
      addon_trading_days_required: data.addon_trading_days_required ?? null,
      addon_discount_percent: data.addon_discount_percent ?? null,
      addon_valid_for_days: data.addon_valid_for_days ?? null,
      addon_extra_amount_allowed_payout:
        data.addon_extra_amount_allowed_payout ?? null,
      addon_extra_allowed_payout_time:
        data.addon_extra_allowed_payout_time ?? null,
      addon_new_percent_override_default:
        data.addon_new_percent_override_default ?? null,
      addon_disable_percent_default: data.addon_disable_percent_default ?? null,
      allow_reset_of_packages: compact(data.allow_reset_of_packages).length
        ? compact(data.allow_reset_of_packages)
        : null,
      addon_additional_drawdown_option: data.addon_additional_drawdown_option
        ? Number(data.addon_additional_drawdown_option)
        : null,
      market_data_type: data.market_data_type ?? null,
      performance_account_type: data.performance_account_type ?? null,
      // tradovate_financial_template:
      //   !isNonTrading && data.tradovate_financial_template
      //     ? Number(data.tradovate_financial_template)
      //     : null,
      // dxfeed_financial_template: !isNonTrading
      //   ? data.dxfeed_financial_template
      //   : null,
      competition_activation_date: data.competition_activation_date ?? null,
    },
    billing_plan: {
      name: data.name,
      addon_monthly_price: data.addon_monthly_price,
      currency: data.currency,
      first_price: data.first_price,
      first_period_value: data.first_period_value ?? null,
      first_period_unit: data.first_period_unit,
      rebill_times_type: data.rebill_times_type ?? null,
      rebill_times_unit: data.rebill_times_unit,
      second_price: !isDisableSecondBilling ? data.second_price : null,
      second_period_value: !isDisableSecondBilling
        ? data.second_period_value
        : null,
      second_period_unit: !isDisableSecondBilling
        ? data.second_period_unit
        : null,
      quantity: data.quantity ?? 0,
      change_quantity: data.change_quantity ?? 0,
      expire_in_last_day_of_current_month:
        data.expire_in_last_day_of_current_month,
      is_apply_convenience_fee: data.is_apply_convenience_fee,
      is_apply_tax: data.is_apply_tax,
      is_enabled: data.is_enabled,
    },
    payout_logics: payoutLogics,
    instrument: !isNonTrading
      ? {
          product_id: productId ? Number(productId) : undefined,
          rithmic_standard_package: data.rithmic_standard_package,
          rithmic_performance_package: data.rithmic_performance_package,
          package_rms_content_standard: data.package_rms_content_standard,
          package_rms_content_performance: data.package_rms_content_performance,
        }
      : null,
  };
};

export const mapperProductData = (product: ProductOffer): ProductFormData => {
  return {
    account_benefits: product.account_benefits ?? undefined,
    account_type: product.account_type ?? undefined,
    competition_activation_date:
      product.competition_activation_date ?? undefined,
    payout_date_eligible_type: product.payout_date_eligible_type ?? undefined,
    accounts_in_bundle: product.accounts_in_bundle ?? undefined,
    weekly_account_payout_max: product.weekly_account_payout_max ?? undefined,
    action_after_payout: product.action_after_payout ?? undefined,
    addon_disable_at: product.addon_disable_at ?? undefined,
    addon_disable_percent_default:
      product.addon_disable_percent_default ?? undefined,
    addon_discount_percent: product.addon_discount_percent ?? undefined,
    addon_extra_allowed_payout_time:
      product.addon_extra_allowed_payout_time ?? undefined,
    addon_extra_amount_allowed_payout:
      product.addon_extra_amount_allowed_payout ?? undefined,
    addon_additional_drawdown_option:
      product.addon_additional_drawdown_option ?? undefined,
    addon_lifetime_performance_account_package_id:
      product.addon_lifetime_performance_account_package_id?.toString(),
    addon_new_percent_override_default:
      product.addon_new_percent_override_default ?? undefined,
    addon_packages: product.addon_packages ?? undefined,
    addon_trading_days_required:
      product.addon_trading_days_required ?? undefined,
    addon_type: product.addon_type ?? undefined,
    addon_valid_for_days: product.addon_valid_for_days ?? undefined,
    allow_reset_of_packages: product.allow_reset_of_packages ?? undefined,
    auto_liquidate: product.auto_liquidate ?? undefined,
    name: product.billing_plan?.name ?? undefined,
    currency: product.billing_plan?.currency ?? undefined,
    first_price: product.billing_plan?.first_price ?? undefined,
    first_period_value: product.billing_plan?.first_period_value ?? undefined,
    first_period_unit: product.billing_plan?.first_period_unit ?? undefined,
    rebill_times_type: product.billing_plan?.rebill_times_type ?? undefined,
    rebill_times_unit: product.billing_plan?.rebill_times_unit ?? undefined,
    second_period_value: product.billing_plan?.second_period_value ?? undefined,
    second_price: product.billing_plan?.second_price ?? undefined,
    addon_monthly_price: product.billing_plan?.addon_monthly_price ?? undefined,
    second_period_unit: product.billing_plan?.second_period_unit ?? undefined,
    quantity: product.billing_plan?.quantity ?? undefined,
    change_quantity: product.billing_plan?.change_quantity ?? undefined,
    expire_in_last_day_of_current_month:
      product.billing_plan?.expire_in_last_day_of_current_month ?? undefined,
    is_apply_convenience_fee:
      product.billing_plan?.is_apply_convenience_fee ?? undefined,
    is_apply_tax: product.billing_plan?.is_apply_tax ?? undefined,
    is_enabled: product.billing_plan?.is_enabled ?? undefined,
    cash_in_hand: product.cash_in_hand ?? undefined,
    category: product.category ?? undefined,
    compatible_data_feeds: product.compatible_data_feeds ?? undefined,
    days_to_qualify: product.days_to_qualify ?? undefined,
    description: product.description ?? undefined,
    disable_product_link: product.disable_product_link ?? undefined,
    disallow_product_ids: product.disallow_product_ids ?? undefined,
    display_name_app: product.display_name_app ?? undefined,
    display_name_web: product.display_name_web ?? undefined,
    drawdown: product.drawdown ?? undefined,
    drawdown_type: product.drawdown_type ?? undefined,
    fill_out_survey: product.fill_out_survey ?? undefined,
    internal_notes: product.internal_notes ?? undefined,
    is_active: product.is_active ?? undefined,
    is_performance_account: product.is_performance_account ?? undefined,
    mailchimp_list_segments: product.mailchimp_list_segments ?? undefined,
    market_data_type: product.market_data_type ?? undefined,
    performance_account_type: product.performance_account_type ?? undefined,
    max_payouts_per_account: product.max_payouts_per_account ?? undefined,
    offer_reset_practice_account_phase:
      product.offer_reset_practice_account_phase ?? undefined,
    offer_reset_performance_account_phase:
      product.offer_reset_performance_account_phase ?? undefined,
    payout_logics: product.payout_logics,
    payout_request_trigger: product.payout_request_trigger ?? 'last_saturday',
    performance_best_day_profit_enabled:
      product.performance_best_day_profit_enabled ?? undefined,
    performance_contract_usage_enabled:
      product.performance_contract_usage_enabled ?? undefined,
    performance_profit_withdrawal_enabled:
      product.performance_profit_withdrawal_enabled ?? undefined,
    practice_best_day_profit_enabled:
      product.practice_best_day_profit_enabled ?? undefined,
    practice_contract_usage_enabled:
      product.practice_contract_usage_enabled ?? undefined,
    practice_profit_withdrawal_enabled:
      product.practice_profit_withdrawal_enabled ?? undefined,
    profit_target: product.profit_target ?? undefined,
    required_product_ids: product.required_product_ids ?? undefined,
    start_date_calculation: product.start_date_calculation ?? undefined,
    survey_trigger_time: product.survey_trigger_time ?? undefined,
    title: product.title ?? undefined,
    trailing_drawdown_stops: product.trailing_drawdown_stops ?? undefined,
    trailing_drawdown_stop_at: product.trailing_drawdown_stop_at ?? undefined,
    pa_account_trailing_drawdown_stop_at:
      product.pa_account_trailing_drawdown_stop_at ?? undefined,
    type: product.type ?? undefined,
    is_bundle: product.is_bundle ?? undefined,
    is_visible_on_web: product.is_visible_on_web ?? undefined,
    affiliate_eligible: product.affiliate_eligible ?? undefined,
    company: TradingPlatForms.RITHMIC_DATA_FEED,
    // tradovate_financial_template:
    //   product.tradovate_financial_template ?? undefined,
    // dxfeed_financial_template: product.dxfeed_financial_template ?? undefined,
    practice_contract_not_exceed_percent:
      product.practice_contract_not_exceed_percent ?? undefined,
    practice_contract_average_days:
      product.practice_contract_average_days ?? undefined,
    practice_profit_not_exceed_percent:
      product.practice_profit_not_exceed_percent ?? undefined,
    practice_profit_average_days:
      product.practice_profit_average_days ?? undefined,
    practice_best_day_profit_not_exceed_percent:
      product.practice_best_day_profit_not_exceed_percent ?? undefined,
    practice_best_day_profit: product.practice_best_day_profit ?? undefined,
    performance_contract_not_exceed_percent:
      product.performance_contract_not_exceed_percent ?? undefined,
    performance_contract_average_days:
      product.performance_contract_average_days ?? undefined,
    performance_profit_not_exceed_percent:
      product.performance_profit_not_exceed_percent ?? undefined,
    performance_profit_average_days:
      product.performance_profit_average_days ?? undefined,
    performance_best_day_profit_not_exceed_percent:
      product.performance_best_day_profit_not_exceed_percent ?? undefined,
    performance_best_day_profit:
      product.performance_best_day_profit ?? undefined,
    rithmic_standard_package:
      product.instrument?.rithmic_standard_package ?? undefined,
    rithmic_performance_package:
      product.instrument?.rithmic_performance_package ?? undefined,
    package_rms_content_standard:
      product.instrument?.package_rms_content_standard ?? undefined,
    package_rms_content_performance:
      product.instrument?.package_rms_content_performance ?? undefined,
  } as ProductFormData;
};

export const buildProductAddons = (data: AddonFormData): ProductAddonsReq => {
  return {
    title: data.title,
    price: data.price,
    addon_lifetime_performance_account_package_id:
      data.addon_lifetime_performance_account_package_id
        ? Number(data.addon_lifetime_performance_account_package_id)
        : undefined,
    addon_trading_days_required: data.addon_trading_days_required,
    addon_discount_percent: data.addon_discount_percent,
    addon_valid_for_days: data.addon_valid_for_days,
    addon_extra_amount_allowed_payout: data.addon_extra_amount_allowed_payout,
    addon_new_percent_override_default: data.addon_new_percent_override_default,
    addon_extra_allowed_payout_time: data.addon_extra_allowed_payout_time,
    addon_disable_percent_default: data.addon_disable_percent_default,
    addon_packages: data.addon_packages,
    addon_additional_drawdown_option: Number(
      data.addon_additional_drawdown_option
    ),
    addon_performance_account_fee_option:
      data.addon_performance_account_fee_option,
  };
};

export const mapperProductAddons = (data: ProductOffer): AddonFormData => {
  const addonAdditionalDrawdownOption = data.addon_additional_drawdown_option;

  const normalizedAddonAdditionalDrawdownOption =
    addonAdditionalDrawdownOption === null ||
    addonAdditionalDrawdownOption === undefined
      ? undefined
      : Number.isFinite(Number(addonAdditionalDrawdownOption))
        ? String(Number(addonAdditionalDrawdownOption))
        : String(addonAdditionalDrawdownOption);

  return {
    title: data.title ?? undefined,
    price: data.billing_plan?.first_price ?? undefined,
    addon_type: data.addon_type ?? undefined,
    addon_additional_drawdown_option: normalizedAddonAdditionalDrawdownOption,
    addon_lifetime_performance_account_package_id:
      data.addon_lifetime_performance_account_package_id?.toString(),
    addon_trading_days_required: data.addon_trading_days_required ?? undefined,
    addon_discount_percent: data.addon_discount_percent ?? undefined,
    addon_valid_for_days: data.addon_valid_for_days ?? undefined,
    addon_extra_amount_allowed_payout:
      data.addon_extra_amount_allowed_payout ?? undefined,
    addon_new_percent_override_default:
      data.addon_new_percent_override_default ?? undefined,
    addon_extra_allowed_payout_time:
      data.addon_extra_allowed_payout_time ?? undefined,
    addon_disable_percent_default:
      data.addon_disable_percent_default ?? undefined,
    addon_packages: data.addon_packages ?? undefined,
  } as AddonFormData;
};

export const buildProductReset = (data: ResetFormData): ProductResetReq => {
  return {
    title: data.title,
    price: data.price,
    allow_reset_of_packages: compact(data.allow_reset_of_packages).length
      ? compact(data.allow_reset_of_packages)
      : undefined,
  };
};
export const mapperProductResets = (data: ProductOffer): ResetFormData => {
  return {
    title: data.title ?? undefined,
    price: data.billing_plan?.first_price ?? undefined,
    allow_reset_of_packages: data.allow_reset_of_packages ?? undefined,
  } as ResetFormData;
};

export const buildProductMarketData = (
  data: MarketDataFormData
): ProductMarketDataReq => {
  return {
    title: data.title,
    price: data.price,
    market_data_type: data.market_data_type,
  };
};
export const mapperProductMarketData = (
  data: ProductOffer
): MarketDataFormData => {
  return {
    title: data.title ?? undefined,
    price: data.billing_plan?.first_price ?? undefined,
    market_data_type: data.market_data_type ?? undefined,
  } as MarketDataFormData;
};
